if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      if (this.form) return;

      // A custom element's constructor can run before the parser has added its
      // form children. Defer one task so parser-created product forms can
      // initialize against their complete markup.
      if (!this.hasChildNodes()) {
        window.setTimeout(() => this.init(), 0);
        return;
      }

      this.init();
    }

    init() {
      if (this.form) return;

      const form = this.querySelector('.js-product-form');
      if (form) {
        this.form = form;
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}

(() => {
  function parseStrictFitDimension(rawValue) {
    const cleaned = String(rawValue || '')
      .toLowerCase()
      .replace(/inches|inch|in\.?/g, '')
      .replace(/["”″]/g, '')
      .replace(/-/g, ' ')
      .trim();

    if (!cleaned) return NaN;
    const parts = cleaned.split(/\s+/);
    let total = 0;

    for (const part of parts) {
      if (part.includes('/')) {
        const fraction = part.split('/');
        if (fraction.length !== 2) return NaN;
        const numerator = Number(fraction[0]);
        const denominator = Number(fraction[1]);
        if (!Number.isFinite(numerator) || !Number.isFinite(denominator)
          || denominator <= 0 || numerator < 0 || numerator >= denominator) return NaN;
        total += numerator / denominator;
      } else {
        const number = Number(part);
        if (!Number.isFinite(number) || number < 0) return NaN;
        total += number;
      }
    }

    return total > 0 ? total : NaN;
  }

  function guardPremiumFitData(root = document) {
    root.querySelectorAll('[data-tefs-fit-calculator]').forEach((calculator) => {
      const values = [
        calculator.dataset.requiredFront,
        calculator.dataset.requiredBack,
        calculator.dataset.requiredHeight,
        calculator.dataset.requiredDepth
      ].map(parseStrictFitDimension);

      if (values.every((value) => Number.isFinite(value))) return;

      const fallback = document.createElement('div');
      fallback.className = 'tefs-product-v3__fit-missing-data';
      fallback.innerHTML = '<strong>Fit checking is available with expert assistance.</strong>'
        + '<p>One or more verified fit dimensions for this model cannot be safely interpreted. Send us your measurements and a photo and we’ll help confirm compatibility.</p>'
        + '<a href="/pages/contact-us">Send Measurements to an Expert</a>';
      calculator.replaceWith(fallback);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => guardPremiumFitData());
  } else {
    guardPremiumFitData();
  }

  document.addEventListener('shopify:section:load', (event) => {
    guardPremiumFitData(event.target || document);
  });
})();
